import { CITY_CENTER, WORLD_SIZE } from './constants';
import { dist2, hash2 } from './math';

/**
 * Terrain regions, laid out as a Voronoi diagram over the map so each biome is
 * a large organic patch rather than a checkerboard. Fully deterministic - no
 * `Math.random` - so the client and server could look at the same coordinates
 * and agree, exactly like the rest of the world generation.
 *
 * Only the look of the ground uses this today, but keeping it in `shared/`
 * means biomes can later drive monster themes or resource spawns for free.
 */
export type BiomeId = 'plains' | 'forest' | 'autumn' | 'desert' | 'snow' | 'swamp' | 'highlands';

export interface BiomeDef {
  id: BiomeId;
  name: string;
  /** Base ground colour, blended into the terrain map. */
  color: string;
  /** Relative chance of being picked for a region. */
  weight: number;
}

export const BIOMES: Record<BiomeId, BiomeDef> = {
  plains: { id: 'plains', name: 'Plains', color: '#3c5a35', weight: 5 },
  forest: { id: 'forest', name: 'Deepwood', color: '#28422d', weight: 4 },
  autumn: { id: 'autumn', name: 'Autumnwood', color: '#5f4826', weight: 3 },
  desert: { id: 'desert', name: 'Dustlands', color: '#7b6b3c', weight: 2 },
  snow: { id: 'snow', name: 'Frostlands', color: '#8b98a7', weight: 2 },
  swamp: { id: 'swamp', name: 'Mire', color: '#36432e', weight: 2 },
  highlands: { id: 'highlands', name: 'Highlands', color: '#575b50', weight: 3 },
};

/** Biome regions are this many units apart; big enough to travel through. */
const BIOME_CELL = 6500;
/** Keep the starting country familiar and green, regardless of the hash. */
const HOME_GREEN_RADIUS = 6200;

const BIOME_ORDER = Object.values(BIOMES);
const WEIGHTED: BiomeId[] = BIOME_ORDER.flatMap((def) => Array<BiomeId>(def.weight).fill(def.id));
const TOTAL_WEIGHT = WEIGHTED.length;

interface BiomeSeed {
  x: number;
  y: number;
  biome: BiomeId;
}

/** Seeds are looked up many times when building the terrain map, so cache them. */
const seedCache = new Map<number, BiomeSeed>();

function seedFor(cx: number, cy: number): BiomeSeed {
  const key = cx * 100003 + cy;
  const cached = seedCache.get(key);
  if (cached) return cached;

  const x = (cx + 0.15 + hash2(cx, cy, 101) * 0.7) * BIOME_CELL;
  const y = (cy + 0.15 + hash2(cx, cy, 211) * 0.7) * BIOME_CELL;
  const roll = Math.floor(hash2(cx, cy, 307) * TOTAL_WEIGHT);
  let biome = WEIGHTED[Math.min(roll, TOTAL_WEIGHT - 1)];

  // The hub and its hunting grounds are always plains, so new players start
  // somewhere welcoming instead of a frostfield.
  if (dist2(x, y, CITY_CENTER.x, CITY_CENTER.y) < HOME_GREEN_RADIUS * HOME_GREEN_RADIUS) {
    biome = 'plains';
  }

  const seed: BiomeSeed = { x, y, biome };
  seedCache.set(key, seed);
  return seed;
}

/** The biome at a world position. Nearest seed wins, as in a Voronoi diagram. */
export function biomeAt(x: number, y: number): BiomeId {
  if (x < 0 || y < 0 || x > WORLD_SIZE || y > WORLD_SIZE) return 'plains';
  const cx = Math.floor(x / BIOME_CELL);
  const cy = Math.floor(y / BIOME_CELL);
  let best: BiomeSeed | undefined;
  let bestD = Infinity;
  for (let oy = -1; oy <= 1; oy++) {
    for (let ox = -1; ox <= 1; ox++) {
      const seed = seedFor(cx + ox, cy + oy);
      const d = dist2(x, y, seed.x, seed.y);
      if (d < bestD) {
        bestD = d;
        best = seed;
      }
    }
  }
  return best?.biome ?? 'plains';
}

/** Biome colours as packed 0xRRGGBB, for filling the terrain map. */
export const BIOME_RGB: Record<BiomeId, [number, number, number]> = (() => {
  const out = {} as Record<BiomeId, [number, number, number]>;
  for (const def of BIOME_ORDER) {
    const hex = def.color.replace('#', '');
    out[def.id] = [
      parseInt(hex.slice(0, 2), 16),
      parseInt(hex.slice(2, 4), 16),
      parseInt(hex.slice(4, 6), 16),
    ];
  }
  return out;
})();
